import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { navigate } from "gatsby";
import { Drawer, Input, Spin, message } from "antd";
import { isEmpty } from "lodash";
import { UserContext } from "../../../amplify/authenticator-provider";
import {
  GET_ENGAGEMENT_MESSAGE_TEMPLATES,
  GET_ENGAGEMENT_TEMPLATE_TEXT,
} from "../../../graphql/query";
import { AccessPermissionAction } from "../../../library/constants";
import { checkAccessPermission } from "../../../library/helpers";
import AntdIconV4 from "../../../utils/antdIcon";
import useLang from "../../../hooks/useLang";
import { useDevice } from "../../../hooks/useDevice";
import { ROUTES } from "../../../Routes";
import {
  TEMPLATE_ALL_TYPES,
  TEMPLATE_LIST_PAGE_SIZE,
  TEMPLATE_PICKER_DRAWER_WIDTH,
  TEMPLATE_SCOPES,
  TEMPLATE_SORT_FIELDS,
} from "../../../constant/TemplateConstant";
import {
  buildTemplateQueryVariables,
  getTemplateList,
  getTemplateType,
  getTemplateTypeCounts,
} from "../../../helpers/templateHelpers";
import TemplateCategoryChips from "../../commonComponents/TemplateCategoryChips";
import TemplateMessagePreview from "../../commonComponents/TemplateMessagePreview";

/**
 * One template in the picker: what it says, and the one action there is.
 *
 * The name is held to a single line — a long one would push Add out of the
 * row's vertical centre and make a list of templates scan unevenly.
 *
 * Add is drawn rather than composed from antd's small Button: the design's is
 * 24px tall with 10px of padding and a 6px radius, where antd's small size is
 * 7px and 4px.
 */
const TemplateRow = ({ template, addText, onAdd, disabled }) => (
  <div className="flex items-center gap-3 px-3 py-2.5 border-0 border-b border-solid border-[#f0f0f0] hover:bg-black/[0.02] transition-colors">
    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
      <span className="block w-full text-[14px] leading-[22px] font-medium text-black/[0.88] whitespace-nowrap overflow-hidden text-ellipsis">
        {template.title}
      </span>
      <TemplateMessagePreview message={template.message} />
    </div>
    <button
      type="button"
      disabled={disabled}
      onClick={() => onAdd(template)}
      className="shrink-0 inline-flex items-center justify-center h-6 px-2.5 rounded-md border border-solid border-[#1677ff] bg-[#1677ff] text-[13px] leading-none text-white whitespace-nowrap cursor-pointer transition-colors hover:bg-[#4096ff] hover:border-[#4096ff] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {addText}
    </button>
  </div>
);

const TemplateDrawer = ({
  openTemplateDrawer,
  setOpenTemplateDrawer,
  onApply,
  leadId = null,
  // Off by default: the picker is also used by the campaign builder, the
  // schedule form and the browser-extension pages, where navigating to the
  // Templates tab would take the user out of what they were doing.
  showManage = false,
}) => {
  const canViewAllTemplates = checkAccessPermission(AccessPermissionAction.TEMPLATE_VIEW);
  const canManageTemplates =
    showManage &&
    (canViewAllTemplates || checkAccessPermission(AccessPermissionAction.TEMPLATE_ADD_EDIT));

  // Most callers pass `{ visible }`; the schedule form passes a bare boolean.
  const isOpen = openTemplateDrawer?.visible ?? !!openTemplateDrawer;

  const [lang] = useLang();
  const { isMobile, isTablet } = useDevice();
  const { user } = useContext(UserContext);

  const searchRef = useRef(null);
  const [search, setSearch] = useState("");
  // All, not the first type: the rep opening the picker is looking for a
  // template, not for a category, and starting inside one hides most of them
  // behind a chip they have to notice first.
  const [activeType, setActiveType] = useState(TEMPLATE_ALL_TYPES);
  // The picker is a reading surface, so it asks for one large page of active
  // templates and does its own searching and categorising client-side.
  const variables = useMemo(
    () =>
      buildTemplateQueryVariables({
        // Without the view permission a rep still reaches their own templates;
        // the wider tiers stay behind it, as they did when the picker had
        // scope tabs.
        scope: canViewAllTemplates ? TEMPLATE_SCOPES.ALL : TEMPLATE_SCOPES.MINE,
        companyId: user?.company?.id,
        userId: user?.id,
        isActive: true,
        pageSize: TEMPLATE_LIST_PAGE_SIZE,
        orderBy: TEMPLATE_SORT_FIELDS.UPDATED_ON,
        orderDirection: "desc",
      }),
    [canViewAllTemplates, user?.company?.id, user?.id]
  );

  const { data: resp, loading: templateLoader } = useQuery(GET_ENGAGEMENT_MESSAGE_TEMPLATES, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables,
    skip: !isOpen,
  });

  const templates = useMemo(() => getTemplateList(resp), [resp]);

  /**
   * Categories are counted over everything fetched, not the search result —
   * a chip that changed its count on every keystroke would read as the search
   * having lost templates rather than filtered them.
   */
  const categories = useMemo(() => {
    const byType = getTemplateTypeCounts(templates);
    if (!byType.length) return [];
    // All leads, and is the only chip that is always offered — the per-type
    // ones come and go with what the list actually holds.
    return [{ value: TEMPLATE_ALL_TYPES, label: lang.all, count: templates.length }, ...byType];
  }, [templates, lang.all]);

  // Falls back to All whenever the category being shown stops existing — its
  // last template was deleted, or the list reloaded into a different shape.
  useEffect(() => {
    if (!categories.length) return;
    if (!categories.some(category => category.value === activeType)) {
      setActiveType(TEMPLATE_ALL_TYPES);
    }
  }, [categories, activeType]);

  /**
   * The picker's state outlives a close — the parent owns `visible`, so this
   * component is never unmounted and last time's search and category would
   * still be narrowing the list when it reopens. Cleared on close rather than
   * on open so the list is not seen resetting behind the closing animation.
   */
  useEffect(() => {
    if (isOpen) return;
    setSearch("");
    setActiveType(TEMPLATE_ALL_TYPES);
  }, [isOpen]);

  const isSearching = search.trim().length > 0;

  /**
   * Searching looks across every category, not inside the selected one — a rep
   * who remembers a phrase should not have to guess which chip it was filed
   * under. While a search is running no chip is active, so the list is not
   * claiming to be narrowed by a category it is ignoring.
   */
  const visibleTemplates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle) {
      return templates.filter(
        template =>
          (template?.title || "").toLowerCase().includes(needle) ||
          (template?.message || "").toLowerCase().includes(needle)
      );
    }
    return templates.filter(
      template => activeType === TEMPLATE_ALL_TYPES || getTemplateType(template) === activeType
    );
  }, [templates, activeType, search]);

  const [getMessageText, { loading: msgLoading }] = useLazyQuery(GET_ENGAGEMENT_TEMPLATE_TEXT, {
    fetchPolicy: "network-only",
    onCompleted: response => {
      const result = response?.getEngagementTemplateText;
      if (result?.statusCode === 303) {
        message.error(lang.fillValueBeforeSend);
      } else if (result?.statusCode === 200) {
        message.success(result?.message);
      } else {
        message.error(result?.message);
      }
      onApply(result?.content || "");
      setOpenTemplateDrawer({ visible: false });
    },
    onError: err => {
      message.error(err?.message || lang.somethingWentWrong);
    },
  });

  const onDrawerClose = () => setOpenTemplateDrawer({ visible: false });

  /**
   * Add drops the template straight into the composer — there is no selection
   * to confirm. With a lead in context the server resolves the merge fields
   * first, so the rep sees the real message rather than the raw tokens.
   */
  const handleAdd = template => {
    if (!template) return;
    if (leadId) {
      getMessageText({ variables: { leadId, content: template.message } });
      return;
    }
    onApply(template.message);
    setOpenTemplateDrawer({ visible: false });
  };

  const handleManage = () => {
    setOpenTemplateDrawer({ visible: false });
    navigate(ROUTES.engagementsTemplates);
  };

  return (
    <Drawer
      title={lang.templates}
      placement="right"
      closable
      closeIcon={<AntdIconV4 type="CloseOutlined" style={{ fontSize: 14 }} />}
      width={isMobile ? "100%" : isTablet ? "90%" : TEMPLATE_PICKER_DRAWER_WIDTH}
      onClose={onDrawerClose}
      open={isOpen}
      // The search box is what the picker is for, so it takes focus and the rep
      // can type straight away. Focused here rather than with `autoFocus`: the
      // body mounts before the panel has slid in, and antd moves focus to the
      // drawer itself once it has — which would take it straight back off the
      // input.
      afterOpenChange={opened => {
        if (opened) searchRef.current?.focus();
      }}
      // Managing templates is the only thing the picker links out to; the
      // create and edit affordances live on the Templates tab now. A plain link
      // rather than a link Button — the design's is 14px text on the header's
      // baseline, with none of a button's height or padding.
      extra={
        canManageTemplates && (
          <a
            href={ROUTES.engagementsTemplates}
            // A global stylesheet paints bare anchors the legacy brand navy
            // (#013475); antd's own link colour — and the design's — is #1677ff,
            // which is what every other link in this drawer uses.
            className="text-[14px] leading-[22px] !text-[#1677ff] hover:!text-[#4096ff]"
            onClick={event => {
              // A real href so the link can be opened in a new tab or copied;
              // the click itself routes client-side and closes the picker.
              event.preventDefault();
              handleManage();
            }}
          >
            {lang.manage}
          </a>
        )
      }
      // The drawer's sections carry their own 20px gutter, so the body itself
      // has none: the list scrolls edge to edge while its rows stay inset.
      styles={{
        header: { padding: "16px 20px", borderBottom: "1px solid #f0f0f0" },
        body: { padding: 0, display: "flex", flexDirection: "column" },
      }}
    >
      <div className="flex flex-col gap-2.5 px-5 pt-2 pb-1 shrink-0">
        <Input
          ref={searchRef}
          value={search}
          allowClear
          prefix={<AntdIconV4 type="SearchOutlined" className="text-black/[0.45]" />}
          placeholder={lang.searchTemplates}
          onChange={event => setSearch(event.target.value)}
        />
        <TemplateCategoryChips
          options={categories}
          // No chip is active while searching — the results span every category.
          value={isSearching ? null : activeType}
          onChange={value => {
            setActiveType(value);
            setSearch("");
          }}
        />
      </div>

      <Spin spinning={templateLoader || msgLoading} wrapperClassName="flex-1 min-h-0">
        <div className="flex flex-col px-5 pt-1 pb-4">
          {isEmpty(visibleTemplates) && !templateLoader ? (
            <div className="py-8 text-center text-[13px] text-black/[0.45]">{lang.noTemplates}</div>
          ) : (
            visibleTemplates.map(template => (
              <TemplateRow
                key={template.id}
                template={template}
                addText={lang.add}
                disabled={msgLoading}
                onAdd={handleAdd}
              />
            ))
          )}
        </div>
      </Spin>
    </Drawer>
  );
};

export default TemplateDrawer;
